// Position Summary widget. Body shows whale + liq long/short bars for the
// selected window. Header (PositionSummaryHeader named export) shows a
// compact "N whales • +X long / -Y short" line + the 1h/24h toggle.
//
// Both share state via:
//   - useSharedFlowSummary(coin, window) — one REST poller per (coin, window),
//     consumed by header + body so they stay in sync.
//   - useWidgetSetting('position-summary', 'window', '1h') — picker state
//     persists per tab (was: component-local useState inside useFlowSummary).

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedFlowSummary } from '../Cockpit/data/sharedFlowSummary';
import { useWidgetSetting } from '../Cockpit/cockpitContext.jsx';
import './PositionSummary.css';

const WIDGET_ID = 'position-summary';

function formatDollar(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function Bar({ longUsd, shortUsd }) {
  const total = (Number(longUsd) || 0) + (Number(shortUsd) || 0);
  const longPct = total > 0 ? ((Number(longUsd) || 0) / total) * 100 : 0;
  const shortPct = total > 0 ? ((Number(shortUsd) || 0) / total) * 100 : 0;
  return (
    <div className="ct-ps-bar" data-testid="ps-bar">
      <div className="ct-ps-bar-long" style={{ width: `${longPct}%` }} data-testid="ps-bar-long" />
      <div className="ct-ps-bar-short" style={{ width: `${shortPct}%` }} data-testid="ps-bar-short" />
    </div>
  );
}

export default function PositionSummary({ coin }) {
  const { t } = useTranslation();
  const [windowValue] = useWidgetSetting(WIDGET_ID, 'window', '1h');
  const { data, loading, error } = useSharedFlowSummary(coin, windowValue);

  const whales = data?.whales || { longUsd: 0, shortUsd: 0, count: 0 };
  const liqs = data?.liqs || { longUsd: 0, shortUsd: 0, count: 0 };

  const whalesLabel = t('chartPage.positionSummary.whales', 'Whales');
  const liqsLabel = t('chartPage.positionSummary.liqs', 'Liquidations');
  const longLabel = t('chartPage.positionSummary.long', 'long');
  const shortLabel = t('chartPage.positionSummary.short', 'short');
  const loadingLabel = t('chartPage.positionSummary.loading', 'Loading...');
  const errorLabel = t('chartPage.positionSummary.error', 'Failed to load summary');
  const emptyLabel = t('chartPage.positionSummary.empty', 'No flow data');

  return (
    <div className="ct-position-summary expanded">
      <div className="ct-ps-body" data-testid="ps-body">
        {loading && !data ? (
          <div className="ct-ps-state" data-testid="ps-loading">{loadingLabel}</div>
        ) : error ? (
          <div className="ct-ps-state error" data-testid="ps-error">{errorLabel}</div>
        ) : !data ? (
          <div className="ct-ps-state" data-testid="ps-empty">{emptyLabel}</div>
        ) : (
          <>
            <div className="ct-ps-section" data-testid="ps-whales">
              <div className="ct-ps-section-header">
                <span className="ct-ps-section-title">{whalesLabel}</span>
                <span className="ct-ps-section-count">{whales.count || 0}</span>
              </div>
              <Bar longUsd={whales.longUsd} shortUsd={whales.shortUsd} />
              <div className="ct-ps-section-values">
                <span className="ct-ps-long">+{formatDollar(whales.longUsd)} {longLabel}</span>
                <span className="ct-ps-short">-{formatDollar(whales.shortUsd)} {shortLabel}</span>
              </div>
            </div>

            <div className="ct-ps-section" data-testid="ps-liqs">
              <div className="ct-ps-section-header">
                <span className="ct-ps-section-title">{liqsLabel}</span>
                <span className="ct-ps-section-count">{liqs.count || 0}</span>
              </div>
              <Bar longUsd={liqs.longUsd} shortUsd={liqs.shortUsd} />
              <div className="ct-ps-section-values">
                <span className="ct-ps-long">+{formatDollar(liqs.longUsd)} {longLabel}</span>
                <span className="ct-ps-short">-{formatDollar(liqs.shortUsd)} {shortLabel}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Compact header strip: 1h/24h toggle only. The count/long/short numbers
// live in the body's first row (KITЫ section header + values), so we don't
// duplicate them up here.
export const PositionSummaryHeader = React.memo(function PositionSummaryHeader() {
  const [windowValue, setWindow] = useWidgetSetting(WIDGET_ID, 'window', '1h');
  return (
    <div className="ct-ps-window-toggle ct-ps-window-toggle-header" role="tablist">
      {['1h', '24h'].map((opt) => (
        <button
          key={opt}
          type="button"
          role="tab"
          aria-selected={windowValue === opt}
          className={`ct-ps-window-btn ${windowValue === opt ? 'active' : ''}`}
          onClick={() => setWindow(opt)}
          data-testid={`ps-window-${opt}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
});
